import React, { useEffect, useState, useMemo } from "react";
import { ClearRounded } from "@mui/icons-material";
import { useTranslation } from "react-i18next";

export default function PopupFilteringReportWebex({ data, filterValues, handlePostChange, onSubmit, onCancel, people, user }) {
    const [currentPage, setCurrentPage] = useState(1);
    const [postsPerPage] = useState(10);
    const [searchTerm, setSearchTerm] = useState("");
    const [filteredPosts, setFilteredPosts] = useState([]);
    const { t } = useTranslation();

    const uniqueEntries = useMemo(() => {
        const flatPeople = people ? people.flat() : [];
        const flatData = data ? data.flat() : [];
        const canUseCallerId = user && user.holdingId === 33;

        const results = [];
        const seenKeys = new Set();

        flatPeople.forEach(person => {
            const personNumbers = [person.phone?.work, person.phone?.mobile].filter(n => n);
            const personFullName = `${person.firstName} ${person.lastName}`.trim().toLowerCase();

            personNumbers.forEach(pNumber => {
                const matches = flatData.filter(
                    item => item.callingnumber === pNumber || item.originalMatch === pNumber
                );

                if (matches.length === 0) return;

                if (canUseCallerId) {
                    const callerIdNumbers = new Set();

                    matches.forEach(match => {
                        if (match.calleridnumber) {
                            const itemName = (match.callinglineid || "").trim().toLowerCase();
                            const numberMatch = match.callingnumber === pNumber;

                            if (!numberMatch) return; // callingnumber doit toujours correspondre au poste

                            // Si callinglineid est présent, il DOIT correspondre au nom de la personne
                            // Sinon on refuse le calleridnumber → bloque les numéros partagés
                            if (itemName && itemName !== personFullName) return;

                            // callinglineid absent (données incomplètes) → on accepte par fallback
                            // callinglineid présent et correspond → on accepte
                            callerIdNumbers.add(match.calleridnumber);
                        }
                    });

                    // --- Niveau 2 : on groupe par personne avec son numéro de poste + ses numéros affichés ---
                    // Clé unique par personne (id ou nom complet)
                    const personKey = String(person.id || personFullName);

                    if (!seenKeys.has(personKey)) {
                        seenKeys.add(personKey);

                        // On construit le Set de tous les numéros liés à cette personne :
                        // - le numéro de poste (pNumber) — IMPORTANT, toujours inclus
                        // - les calleridnumber validés
                        const allNumbers = new Set([pNumber, ...callerIdNumbers]);

                        results.push({
                            displayNumbers: Array.from(allNumbers),
                            postNumber: pNumber,          // numéro de poste "de base"
                            callerIdNumbers: Array.from(callerIdNumbers), // numéros affichés validés
                            person,
                            isCallerId: callerIdNumbers.size > 0
                        });
                    } else {
                        // La personne existe déjà (plusieurs numéros de poste) → on enrichit
                        const existing = results.find(r => {
                            const rKey = String(r.person.id || `${r.person.firstName} ${r.person.lastName}`.trim().toLowerCase());
                            return rKey === personKey;
                        });
                        if (existing) {
                            existing.displayNumbers = Array.from(new Set([...existing.displayNumbers, pNumber, ...callerIdNumbers]));
                            if (callerIdNumbers.size > 0) existing.isCallerId = true;
                        }
                    }

                } else {
                    // Holding autre : comportement classique, une entrée par numéro
                    if (!seenKeys.has(pNumber)) {
                        seenKeys.add(pNumber);
                        results.push({
                            displayNumbers: [pNumber],
                            postNumber: pNumber,
                            callerIdNumbers: [],
                            person,
                            isCallerId: false
                        });
                    }
                }
            });
        });

        return results;
    }, [people, data, user]);

    useEffect(() => {
        if (searchTerm.trim() === "") {
            setFilteredPosts(uniqueEntries);
        } else {
            const lowerCaseSearchTerm = searchTerm.toLowerCase();
            const filtered = uniqueEntries.filter(entry => {
                const { displayNumbers, person } = entry;

                const matchNumber = displayNumbers.some(num => num.includes(searchTerm));
                const matchFirst = person?.firstName && person.firstName.toLowerCase().includes(lowerCaseSearchTerm);
                const matchLast = person?.lastName && person.lastName.toLowerCase().includes(lowerCaseSearchTerm);

                return matchNumber || matchFirst || matchLast;
            });
            setFilteredPosts(filtered);
        }
        setCurrentPage(1);
    }, [searchTerm, uniqueEntries]);

    const indexOfLastPost = currentPage * postsPerPage;
    const indexOfFirstPost = indexOfLastPost - postsPerPage;
    const currentPosts = filteredPosts.slice(indexOfFirstPost, indexOfLastPost);

    const paginate = (pageNumber) => setCurrentPage(pageNumber);
    const handleResetSearch = () => setSearchTerm("");
    const handleOK = () => {
        // SECURITE : filtrer UNIQUEMENT sur postNumber (le vrai poste de la personne)
        // et jamais sur les displayNumbers/calleridnumbers qui sont partages entre plusieurs personnes.
        // Ancien bug : .filter(entry => entry.displayNumbers.some(num => filterValues.post.includes(num)))
        // faisait remonter Julie PIERRET et Valerie PEREZ quand Annie BERRI etait selectionnee
        // car +33582954300 est partage entre elles -> filterValues.post contenait ce numero -> fuite.
        const selectedByPerson = uniqueEntries
            .filter(entry => filterValues.post.includes(entry.postNumber))
            .map(entry => {
                const personName = `${entry.person.firstName} ${entry.person.lastName}`.trim();
                return {
                    posts: entry.displayNumbers,   // tous les numeros affiches de CETTE personne uniquement
                    postNumber: entry.postNumber,
                    personName,
                    personId: entry.person.id,
                    calltype: ""
                };
            });

        onSubmit(filterValues.post, selectedByPerson);
    };
    const handleCancel = () => onCancel();

    const handleGroupToggle = (displayNumbers) => {
        const allSelected = displayNumbers.every(num => filterValues.post.includes(num));

        displayNumbers.forEach(num => {
            const isSelected = filterValues.post.includes(num);
            if ((!allSelected && !isSelected) || (allSelected && isSelected)) {
                handlePostChange(num);
            }
        });
    };

    const formatDisplay = (entry) => {
        const { displayNumbers, postNumber, callerIdNumbers, isCallerId, person } = entry;
        const name = person ? `${person.firstName} ${person.lastName}` : "";

        if (isCallerId && callerIdNumbers.length > 0) {
            // Affiche : numéro de poste + numéros affichés (marqués ⛔)
            const callerPart = callerIdNumbers.map(n => `⛔ ${n}`).join(", ");
            return `${postNumber} | ${callerPart}`;
        }
        return displayNumbers.join(", ");
    };

    return (
        <div className="popup-filtering-report-webex-statistics">
            <h2>{t("Sélection des valeurs")}</h2>
            <div className="container-search-bar-filtering-report-webex">
                <div>
                    {t("Filtrer par :")}
                    <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    <button type="button" onClick={handleResetSearch}><ClearRounded /></button>
                </div>
            </div>
            <table>
                <thead>
                    <tr>
                        <th></th>
                        <th>{t("Numéro")}</th>
                        <th>{t("Nom")}</th>
                    </tr>
                </thead>
                <tbody>
                    {currentPosts.map((entry, index) => {
                        const { displayNumbers, person } = entry;
                        const firstName = person?.firstName || "N/A";
                        const lastName = person?.lastName || "N/A";

                        const isChecked = displayNumbers.every(num => filterValues.post.includes(num));

                        return (
                            <tr key={`${person?.id || index}-${index}`}>
                                <td>
                                    <input
                                        type="checkbox"
                                        checked={isChecked}
                                        onChange={() => handleGroupToggle(displayNumbers)}
                                    />
                                </td>
                                <td>{formatDisplay(entry)}</td>
                                <td>{firstName} {lastName}</td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>

            {filteredPosts.length > 10 && (
                <div className="pagination">
                    {currentPage > 1 && (
                        <button type="button" onClick={() => paginate(currentPage - 1)}>
                            {t("Précédent")}
                        </button>
                    )}
                    {[...Array(Math.ceil(filteredPosts.length / postsPerPage)).keys()]
                        .slice(Math.max(currentPage - 5, 0), currentPage + 5)
                        .map(number => (
                            <button type="button" key={number} onClick={() => paginate(number + 1)}>
                                {number + 1}
                            </button>
                        ))
                    }
                    {currentPage < Math.ceil(filteredPosts.length / postsPerPage) && (
                        <button type="button" onClick={() => paginate(currentPage + 1)}>
                            {t("Suivant")}
                        </button>
                    )}
                </div>
            )}
            <div className="container-buttons-popup-filtering-report-webex">
                <button type="button" onClick={handleOK}>{t("OK")}</button>
                <button type="button" onClick={handleCancel}>{t("Annuler")}</button>
            </div>
        </div>
    );
}